// Раскладка составного рисунка (части «а», «б» … под одной подписью) - как в отчёте (writer emit_parts/_auto_cols):
// ряд, сетка по cols, столбец или автоматически по размерам частей; масштабы частей в сантиметрах.
import { MAX_H_CM, TEXT_W_CM, naturalCm, widthToCm } from '../util';

export interface PartImage { src: string; width?: string | null; label?: string }
/** Размер части в отчёте до раскладки, см; flex - размер не исходный (снимок шире поля, рамка пустой части). */
export interface PartSize { w: number; h: number; flex: boolean }

const GAP = 0.5;            // см между частями (writer: 180000 EMU)
const LABEL_H = 0.5;        // строка меток

/** Размер части по сведениям об изображении (writer _image_size): ширина из width или по dpi, не шире 17 см, не выше 22. */
export function partSize(info: { width: number; height: number; dpi?: number | null } | null | undefined,
  width: string | null | undefined): PartSize | null {
  if (!info || !info.width || !info.height) return null;
  let w = widthToCm(width ?? null) ?? naturalCm(info.width, info.dpi);
  let h = (w * info.height) / info.width;
  if (w > TEXT_W_CM) { h = (h * TEXT_W_CM) / w; w = TEXT_W_CM; }
  if (h > MAX_H_CM) { w = (w * MAX_H_CM) / h; h = MAX_H_CM; }
  return { w, h, flex: !width && (w >= TEXT_W_CM * 0.99 || h >= MAX_H_CM * 0.99) };
}

const median = (a: number[]) => a.slice().sort((x, y) => x - y)[Math.floor(a.length / 2)];

/** Масштабы частей при c колонках (c >= n - ряд с общим масштабом). */
function scales(S: PartSize[], c: number): number[] {
  const n = S.length;
  if (c >= n) {
    const k = Math.min(1, (TEXT_W_CM - GAP * (n - 1)) / (S.reduce((a, s) => a + s.w, 0) || 1), MAX_H_CM / (Math.max(...S.map((s) => s.h)) || 1));
    return S.map(() => k);
  }
  const cw = (TEXT_W_CM - GAP * (c - 1)) / c;
  return S.map((s) => Math.min(1, cw / (s.w || 1), MAX_H_CM / (s.h || 1)));
}

/** Число колонок без layout/cols (writer _auto_cols): части не мельче 60 % и рисунок на странице; при равенстве -
 *  меньше уменьшены скриншоты, крупнее снимки, компактнее. */
function autoCols(S: PartSize[], labels: boolean): number {
  const n = S.length;
  const plan = (c: number) => {
    const ks = scales(S, c);
    let tot = 0;
    for (let s = 0; s < n; s += c) tot += Math.max(...ks.slice(s, s + c).map((k, i) => S[s + i].h * k)) + (labels ? LABEL_H : 0);
    const fixed = ks.filter((_, i) => !S[i].flex);
    const flex = ks.filter((_, i) => S[i].flex);
    return { c, tot, kmin: fixed.length ? Math.min(...fixed) : 1, kflex: flex.length ? Math.min(...flex) : 1 };
  };
  const plans = Array.from({ length: n }, (_, i) => plan(i + 1));
  const r2 = (x: number) => Math.round(x * 100) / 100;
  const fit = plans.filter((p) => p.tot <= MAX_H_CM && p.kmin >= 0.6);
  if (fit.length) {
    return fit.reduce((b, p) => (r2(p.kmin) > r2(b.kmin) || (r2(p.kmin) === r2(b.kmin) && (r2(p.kflex) > r2(b.kflex)
      || (r2(p.kflex) === r2(b.kflex) && p.c > b.c))) ? p : b)).c;
  }
  const big = plans.filter((p) => p.kmin >= 0.6);
  if (big.length) return Math.max(...big.map((p) => p.c));
  const fits = plans.filter((p) => p.tot <= MAX_H_CM);
  return fits.length ? Math.min(...fits.map((p) => p.c)) : n;
}

/** Раскладка: строки (индексы частей) и ширины частей в см. sizes[i] = null - размер неизвестен или части нет. */
export function planParts(sizes: (PartSize | null)[], layout: string | null | undefined, cols: number | null | undefined,
  labels: boolean): { rows: number[][]; widths: number[]; heights: number[]; cols: number } {
  const real = sizes.filter(Boolean) as PartSize[];
  const frame: PartSize = real.length ? { w: median(real.map((r) => r.w)), h: median(real.map((r) => r.h)), flex: true } : { w: 6, h: 4, flex: true };
  const S = sizes.map((s) => s ?? frame);
  const n = S.length;
  const lay = String(layout || '').toLowerCase();
  let c: number;
  if (lay === 'row') c = n;
  else if (lay === 'column') c = 1;
  else if (lay === 'grid' || (cols && cols > 0)) c = Math.max(1, Math.min(Number(cols) || 2, n));
  else c = autoCols(S, labels);
  const ks = scales(S, c);
  const rows: number[][] = [];
  for (let s = 0; s < n; s += c) rows.push(Array.from({ length: Math.min(c, n - s) }, (_, i) => s + i));
  return { rows, widths: S.map((s, i) => s.w * ks[i]), heights: S.map((s, i) => s.h * ks[i]), cols: c };
}
